import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  UserPlus, Trash2, KeyRound, ShieldCheck, User as UserIcon,
  Loader2, Check, X, AlertTriangle, Store,
} from "lucide-react";
import { storeName } from "../lib/labels";

function StoreChips({ outlets, value, onChange, testPrefix }) {
  const all = value.length === 0;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const base = "px-2.5 py-1 bm-eyebrow border transition-colors";
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-stores`}>
      <button type="button" onClick={() => onChange([])} data-testid={`${testPrefix}-store-all`}
        className={`${base} ${all ? "bg-[#0a0a0a] text-[#f5f2eb] border-[#0a0a0a]" : "border-[#0a0a0a]/40 hover:border-[#0a0a0a]"}`}>
        All stores
      </button>
      {outlets.map((o) => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" onClick={() => toggle(o.id)} data-testid={`${testPrefix}-store-${o.id}`}
            className={`${base} ${on ? "bg-[#c72c2c] text-[#f5f2eb] border-[#c72c2c]" : "border-[#0a0a0a]/40 hover:border-[#0a0a0a]"}`}>
            {storeName(o.name)}
          </button>
        );
      })}
    </div>
  );
}

function storesLabel(outlets, ids) {
  if (!ids?.length) return "All stores";
  return ids.map((id) => storeName(outlets.find((o) => o.id === id)?.name || "?")).join(" + ");
}

function statusLabel(u) {
  if (u.no_password) return { text: "Passwordless", cls: "text-[#b45309]" };
  return { text: "Password set", cls: "text-emerald-700" };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newStores, setNewStores] = useState([]);
  const [adding, setAdding] = useState(false);
  const [outlets, setOutlets] = useState([]);

  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [storesFor, setStoresFor] = useState(null);
  const [storesDraft, setStoresDraft] = useState([]);
  const [busyRow, setBusyRow] = useState(null);

  function load() {
    setLoading(true);
    api.get("/auth/users")
      .then(({ data }) => setUsers(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);
  useEffect(() => { api.get("/outlets").then(({ data }) => setOutlets(data)).catch(() => {}); }, []);

  const flash = (setter, msg) => { setter(msg); setTimeout(() => setter(null), 4000); };

  async function addUser(e) {
    e.preventDefault();
    setAdding(true); setErr(null);
    try {
      const { data } = await api.post("/auth/users", { email, password, outlet_ids: newStores });
      const em = email;
      setEmail(""); setPassword(""); setNewStores([]);
      flash(setOk, data?.invited
        ? `Added ${em} — a set-password email is on its way.`
        : `Added ${em}, but the invite email didn't send. Share a temp password or use "Reset password".`);
      load();
    } catch (e) {
      flash(setErr, formatApiError(e.response?.data?.detail));
    } finally { setAdding(false); }
  }

  async function doReset(em) {
    if ((resetPw || "").length < 6) { flash(setErr, "Password must be at least 6 characters"); return; }
    setBusyRow(em);
    try {
      await api.post(`/auth/users/${encodeURIComponent(em)}/reset-password`, { new_password: resetPw });
      setResetFor(null); setResetPw("");
      flash(setOk, `Password updated for ${em}.`);
      load();
    } catch (e) {
      flash(setErr, formatApiError(e.response?.data?.detail));
    } finally { setBusyRow(null); }
  }

  async function saveStores(em) {
    setBusyRow(em);
    try {
      await api.put(`/auth/users/${encodeURIComponent(em)}/outlets`, { outlet_ids: storesDraft });
      setStoresFor(null);
      flash(setOk, `${em} will now open on ${storesLabel(outlets, storesDraft).toLowerCase()}.`);
      load();
    } catch (e) {
      flash(setErr, formatApiError(e.response?.data?.detail));
    } finally { setBusyRow(null); }
  }

  async function del(em) {
    if (!window.confirm(`Remove ${em}? They lose access immediately.`)) return;
    setBusyRow(em);
    try {
      await api.delete(`/auth/users/${encodeURIComponent(em)}`);
      flash(setOk, `${em} removed.`);
      load();
    } catch (e) {
      flash(setErr, formatApiError(e.response?.data?.detail));
    } finally { setBusyRow(null); }
  }

  const meEmail = (user?.email || "").toLowerCase();

  return (
    <div className="space-y-6 max-w-4xl" data-testid="users-page">
      <section>
        <div className="bm-eyebrow text-neutral-500 mb-1">Toronto · Access Desk</div>
        <h1 className="bm-headline text-4xl">Users &amp; Access</h1>
        <p className="bm-eyebrow text-neutral-500 mt-2">
          Add staff, reset passwords, and remove access. Staff see the dashboard and the shared analyst library; only you manage users. Set a manager's home store and the app opens on that location — they can still widen the store picker to compare with the others. Everyone is signed out automatically after 2 hours of inactivity.
        </p>
      </section>

      {(err || ok) && (
        <div
          data-testid="users-flash"
          className={`px-4 py-2 bm-eyebrow flex items-center gap-2 border ${err ? "border-[#c72c2c] text-[#c72c2c]" : "border-emerald-700 text-emerald-700"}`}
        >
          {err ? <AlertTriangle size={13} /> : <Check size={13} />} {err || ok}
        </div>
      )}

      {/* Add staff */}
      <section className="border border-[#0a0a0a] bg-[#ede8dc] p-6">
        <div className="bm-eyebrow mb-3 flex items-center gap-2"><UserPlus size={13} /> Add a staff member</div>
        <form onSubmit={addUser} className="flex flex-wrap items-end gap-3" data-testid="add-user-form">
          <div className="flex-1 min-w-[220px]">
            <label className="bm-eyebrow text-neutral-500 block mb-1">Email</label>
            <input
              data-testid="add-user-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@blackmarkettoronto.com"
              className="w-full border border-[#0a0a0a] bg-[#f5f2eb] px-3 py-2 text-sm outline-none focus:border-[#c72c2c]"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="bm-eyebrow text-neutral-500 block mb-1">Temp password <span className="text-neutral-400">(optional)</span></label>
            <input
              data-testid="add-user-password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="min 6 chars — leave blank to send via reset"
              className="w-full border border-[#0a0a0a] bg-[#f5f2eb] px-3 py-2 text-sm outline-none focus:border-[#c72c2c]"
            />
          </div>
          <button
            data-testid="add-user-submit"
            type="submit"
            disabled={adding || !email}
            className="inline-flex items-center gap-1.5 px-4 py-2 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:opacity-90 disabled:opacity-40"
          >
            {adding ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
            Add staff
          </button>
        </form>
        <div className="mt-4">
          <label className="bm-eyebrow text-neutral-500 block mb-2 flex items-center gap-1.5"><Store size={12} /> Home store — what they see when they open the app</label>
          <StoreChips outlets={outlets} value={newStores} onChange={setNewStores} testPrefix="add-user" />
        </div>
        <p className="bm-eyebrow text-neutral-400 mt-3">
          On add, we email the staffer a “set your password” link (valid 60 min). A temp password is optional — leave it blank and they set their own from the email.
        </p>
      </section>

      {/* User list */}
      <section className="border border-[#0a0a0a] bg-[#ede8dc]" data-testid="users-list">
        <div className="bm-eyebrow px-6 py-3 border-b border-[#0a0a0a]/30 flex items-center justify-between">
          <span>{users.length} {users.length === 1 ? "user" : "users"}</span>
          {loading && <Loader2 size={13} className="animate-spin text-neutral-500" />}
        </div>
        <div className="divide-y divide-[#0a0a0a]/15">
          {users.map((u) => {
            const isOwner = u.role === "owner";
            const isSelf = u.email.toLowerCase() === meEmail;
            const st = statusLabel(u);
            const busy = busyRow === u.email;
            return (
              <div key={u.email} className="px-6 py-4" data-testid={`user-row-${u.email}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    {isOwner ? <ShieldCheck size={18} className="text-[#c72c2c] shrink-0" /> : <UserIcon size={18} className="text-neutral-500 shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0a0a0a] truncate">
                        {u.email}{isSelf && <span className="bm-eyebrow text-neutral-400 ml-2">you</span>}
                      </div>
                      <div className="bm-eyebrow text-neutral-500 mt-0.5 flex items-center gap-3">
                        <span className={isOwner ? "text-[#c72c2c]" : ""}>{u.role}</span>
                        <span className={st.cls}>{st.text}</span>
                        {!isOwner && (
                          <span className="flex items-center gap-1" data-testid={`user-stores-${u.email}`}>
                            <Store size={11} /> {storesLabel(outlets, u.outlet_ids)}
                          </span>
                        )}
                        <span className="text-neutral-400 font-mono-bm">added {fmtDate(u.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isOwner && (
                      <button
                        data-testid={`user-stores-edit-${u.email}`}
                        onClick={() => { setStoresFor(storesFor === u.email ? null : u.email); setStoresDraft(u.outlet_ids || []); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
                      >
                        <Store size={12} /> Stores
                      </button>
                    )}
                    <button
                      data-testid={`user-reset-${u.email}`}
                      onClick={() => { setResetFor(resetFor === u.email ? null : u.email); setResetPw(""); }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
                    >
                      <KeyRound size={12} /> Reset password
                    </button>
                    {!isOwner && !isSelf && (
                      <button
                        data-testid={`user-delete-${u.email}`}
                        onClick={() => del(u.email)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bm-eyebrow border border-[#c72c2c] text-[#c72c2c] hover:bg-[#c72c2c]/10 disabled:opacity-40"
                      >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Remove
                      </button>
                    )}
                  </div>
                </div>

                {storesFor === u.email && (
                  <div className="mt-3 flex items-center gap-3 flex-wrap border-t border-[#0a0a0a]/15 pt-3" data-testid={`user-stores-form-${u.email}`}>
                    <StoreChips outlets={outlets} value={storesDraft} onChange={setStoresDraft} testPrefix={`user-${u.email}`} />
                    <button
                      data-testid={`user-stores-save-${u.email}`}
                      onClick={() => saveStores(u.email)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:opacity-90 disabled:opacity-40"
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                    </button>
                    <button onClick={() => setStoresFor(null)} className="inline-flex items-center gap-1.5 px-3 py-2 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5">
                      <X size={12} /> Cancel
                    </button>
                  </div>
                )}

                {resetFor === u.email && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap border-t border-[#0a0a0a]/15 pt-3" data-testid={`user-reset-form-${u.email}`}>
                    <input
                      data-testid={`user-reset-input-${u.email}`}
                      type="text"
                      value={resetPw}
                      onChange={(e) => setResetPw(e.target.value)}
                      placeholder="New password (min 6 chars)"
                      className="flex-1 min-w-[220px] border border-[#0a0a0a] bg-[#f5f2eb] px-3 py-2 text-sm outline-none focus:border-[#c72c2c]"
                    />
                    <button
                      data-testid={`user-reset-save-${u.email}`}
                      onClick={() => doReset(u.email)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bm-eyebrow border border-[#0a0a0a] bg-[#0a0a0a] text-[#f5f2eb] hover:opacity-90 disabled:opacity-40"
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                    </button>
                    <button
                      onClick={() => { setResetFor(null); setResetPw(""); }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bm-eyebrow border border-[#0a0a0a] hover:bg-[#0a0a0a]/5"
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {!loading && users.length === 0 && (
            <div className="px-6 py-8 text-sm text-neutral-500">No users yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}
